import React, { useEffect } from 'react';
import cn from 'classnames';
import { BBStates, EditorMode, StoryMediaPlatform } from 'types/story';
import { convertPxToVw } from 'utils/helpers';
import { COMPONENT_STATES } from 'src/common/constants';
import { ANSWER_INPUT_MESSAGE_DATA_ATTR } from './constants';
import { StateMessageEditable } from './StateMessageEditable';
import css from './AnswerInput.scss';

type StateMessageProps = {
	isEditableMode: boolean;
	text: string;
	currentState: BBStates;
	currentMediaQuery: StoryMediaPlatform;
	path?: string;
	isHidden: boolean;
	editorMode: EditorMode;
};

type StateMessageNodeProps = {
	ref: React.RefObject<HTMLParagraphElement>;
	className: string;
	'data-selector': string;
};

const useMarginUpdate = (nodeRef: React.RefObject<HTMLParagraphElement>) => {
	const updateMargin = React.useCallback(() => {
		const node = nodeRef.current;
		if (!node) return;

		const parent = node.parentNode;
		const nodeHeight = window.getComputedStyle(node).height;
		const marginInVw = convertPxToVw(nodeHeight);

		if (parent) {
			(parent as HTMLElement).style.marginBlockEnd = parseInt(marginInVw, 10) === 0 ? '' : marginInVw;
		}
	}, [nodeRef]);

	useEffect(() => {
		const node = nodeRef.current;
		if (!node) return () => {};

		// Handle element size changes
		const resizeObserver = new ResizeObserver(updateMargin);
		resizeObserver.observe(node);

		// Initial update
		updateMargin();

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener('resize', updateMargin);

			const parent = node.parentNode as HTMLElement;
			if (parent) {
				parent.style.marginBlockEnd = '';
			}
		};
	}, [updateMargin, nodeRef]);
};

export const StateMessage: React.FC<StateMessageProps> = ({
	path = '',
	currentState,
	currentMediaQuery,
	isEditableMode,
	isHidden,
	text,
	editorMode,
}) => {
	const nodeRef = React.useRef<HTMLParagraphElement>(null);
	useMarginUpdate(nodeRef);

	const isEditableState = currentState !== COMPONENT_STATES.DEFAULT && isEditableMode;
	const nodeProps: StateMessageNodeProps = {
		'data-selector': ANSWER_INPUT_MESSAGE_DATA_ATTR,
		ref: nodeRef,
		className: cn(css.stateMessage, { [css.hidden]: isHidden }),
	};

	if (isEditableState) {
		return (
			<div {...nodeProps}>
				<StateMessageEditable
					path={path}
					currentState={currentState}
					currentMediaQuery={currentMediaQuery}
					text={text}
					editorMode={editorMode}
				/>
			</div>
		);
	}

	// eslint-disable-next-line react/no-danger
	return <div {...nodeProps} dangerouslySetInnerHTML={{ __html: text }} />;
};
